import { useState, type ChangeEvent } from 'react'
import type { AdminClientSummary } from '../../clients/index.js'
import { ADMIN_API_BASE_URL, AUTHORIZATION_SERVER_ISSUER, EndpointHint } from '../../core/index.js'
import { loadPartnerKeyFile, type LoadedPartnerKey } from '../lib/partnerKeyFile.js'
import { requestPartnerToken, type PartnerSession } from '../lib/partnerApi.js'
import { Plug } from 'lucide-react'
import { ErrorNote, INPUT_CLASS, Labeled, describeError } from './formControls.js'
import { Button } from '../../core/index.js'

// Guards against choosing the wrong file: a key file is a few hundred bytes.
const MAX_KEY_FILE_BYTES = 16 * 1024

function SessionSummary({ session, kid }: { session: PartnerSession; kid: string }) {
  return (
    <dl className="mt-4 border border-marine/15 bg-canvas p-4 grid gap-x-6 gap-y-2 sm:grid-cols-2 text-xs">
      <div>
        <dt className="font-bold text-ink-mid">Acting as client</dt>
        <dd className="font-mono text-ink break-all">{session.clientId}</dd>
      </div>
      <div>
        <dt className="font-bold text-ink-mid">Signed with key</dt>
        <dd className="font-mono text-ink break-all">{kid}</dd>
      </div>
      <div>
        <dt className="font-bold text-ink-mid">Scopes granted</dt>
        <dd className="font-mono text-ink">{session.scopes.length > 0 ? session.scopes.join(' ') : '(none)'}</dd>
      </div>
      <div>
        <dt className="font-bold text-ink-mid">Token expires</dt>
        <dd className="text-ink">{new Date(session.expiresAt).toLocaleTimeString()}</dd>
      </div>
      <div className="sm:col-span-2">
        <dt className="font-bold text-ink-mid">Token claims</dt>
        <dd>
          <pre className="font-mono text-[11px] text-ink whitespace-pre-wrap break-all">{JSON.stringify(session.claims, null, 2)}</pre>
        </dd>
      </div>
    </dl>
  )
}

interface PartnerConnectionProps {
  /** Onboarded clients only - a registration without a clientId cannot request a token. */
  clients: AdminClientSummary[]
  clientId: string
  session: PartnerSession | null
  onClientChange: (clientId: string) => void
  onSession: (session: PartnerSession | null) => void
}

/**
 * Step 1 of the simulator: become a partner. Loads the partner's key file
 * into this page and runs the `client_credentials` grant with it, exactly as
 * the partner's backend would. The key never leaves the browser.
 */
export function PartnerConnection({ clients, clientId, session, onClientChange, onSession }: PartnerConnectionProps) {
  const [key, setKey] = useState<LoadedPartnerKey | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)

  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]
    setKey(null)
    setError(null)
    onSession(null)
    if (!file) {
      return
    }
    if (file.size > MAX_KEY_FILE_BYTES) {
      setError('This file is too large to be a partner key file.')
      return
    }
    try {
      setKey(await loadPartnerKeyFile(await file.text()))
    } catch (err) {
      setError(describeError(err, 'This key file could not be read.'))
    }
  }

  async function handleRequestToken(): Promise<void> {
    if (!key || clientId === '' || !ADMIN_API_BASE_URL) {
      return
    }
    setIsRequesting(true)
    setError(null)
    try {
      onSession(await requestPartnerToken(ADMIN_API_BASE_URL, AUTHORIZATION_SERVER_ISSUER, clientId, key, ''))
    } catch (err) {
      onSession(null)
      setError(describeError(err, 'The token request failed.'))
    } finally {
      setIsRequesting(false)
    }
  }

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2 mb-4">
        <Labeled label="Client" htmlFor="simulator-client" hint="The partner whose system you are playing.">
          <select id="simulator-client" className={INPUT_CLASS} value={clientId} disabled={isRequesting} onChange={(event) => onClientChange(event.target.value)}>
            <option value="">Choose a client…</option>
            {clients.map((client) => (
              <option key={client.registrationId} value={client.clientId ?? ''}>
                {client.organizationName} ({client.environment})
              </option>
            ))}
          </select>
        </Labeled>
        <Labeled
          label="Partner key file"
          htmlFor="simulator-key"
          hint="The .json key downloaded when the client was onboarded. It is used in this browser only and is never uploaded."
        >
          <input id="simulator-key" type="file" accept="application/json,.json" className="block w-full text-xs text-ink" disabled={isRequesting} onChange={(event) => void handleFile(event)} />
        </Labeled>
      </div>

      <Button icon={Plug} size="lg" disabled={!key || clientId === '' || isRequesting} onClick={() => void handleRequestToken()}>
        {isRequesting ? 'Requesting token…' : session ? 'Request a new token' : 'Request token'}
      </Button>
      <EndpointHint className="ml-3">POST /oauth/token</EndpointHint>

      {error && (
        <div className="mt-4">
          <ErrorNote message={error} />
        </div>
      )}
      {session && key && <SessionSummary session={session} kid={key.kid} />}
    </div>
  )
}
